import { useState } from 'react';
import { middayOfDate, middayOfTodayDate, startOfDateUtc } from '../../helpers/date';
import { IShopifySettingsPutRequest } from '../../models';

export interface IUseDropdownDatePicker {
    settings: IShopifySettingsPutRequest;
    integrationLang: Record<string, any>;
    onDateRangeSelect: (from: Date | null, to: Date | null) => void;
    maxEnabledDays: number;
}

export function useDropdownDatePicker(props: IUseDropdownDatePicker) {
    const { settings, integrationLang, onDateRangeSelect, maxEnabledDays } = props;

    const getButtonText = (from: Date | null, to: Date | null) => {
        if (!from && !to) {
            return integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_RANGE_DATE_PICKER_PLACEHOLDER;
        } else if (from?.toDateString() === to?.toDateString() || !to) {
            return (
                integrationLang.SETUP_PAGE.SYNC_TAB.FOR +
                middayOfDate(syncFromDateUtc)?.toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                })
            );
        } else {
            return (
                integrationLang.SETUP_PAGE.SYNC_TAB.FROM +
                middayOfDate(syncFromDateUtc)?.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                }) +
                integrationLang.SETUP_PAGE.SYNC_TAB.TO +
                middayOfDate(syncToDateUtc)?.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                })
            );
        }
    };

    const syncFromDateUtc = settings.syncFromDateUtc ? new Date(settings.syncFromDateUtc) : null;
    const syncToDateUtc = settings.syncToDateUtc ? new Date(settings.syncToDateUtc) : null;

    const [buttonText, setButtonText] = useState(getButtonText(syncFromDateUtc, syncToDateUtc));

    const selectedRange = () => ({
        // Convert the stored UTC date to render nicely in the XUI datepicker
        from: middayOfDate(syncFromDateUtc) ?? middayOfTodayDate(),
        to: middayOfDate(syncToDateUtc) ?? middayOfTodayDate(),
    });

    const getDateDiff = (from: Date, to?: Date) =>
        parseInt((((to?.valueOf() ?? middayOfTodayDate().valueOf()) - from.valueOf()) / 86400000).toString(), 10);

    const handleDateRangeOnBlur = () => {
        if (getDateDiff(selectedRange().from, selectedRange().to) > maxEnabledDays) {
            setButtonText(integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_RANGE_DATE_PICKER_PLACEHOLDER);
            settings.syncFromDateUtc = null;
        } else {
            setButtonText(getButtonText(syncFromDateUtc, syncToDateUtc));
        }
    };

    const setSelectedRange = (from: Date, to: Date) => {
        // We want to send the date to the backend as start of date UTC
        onDateRangeSelect(startOfDateUtc(from), startOfDateUtc(to));
    };

    return { selectedRange, buttonText, setButtonText, getDateDiff, handleDateRangeOnBlur, setSelectedRange };
}
